import { useState } from 'react';
import { Carousel, Col, Container, Image, Row } from 'react-bootstrap';
import { PageMeta } from '../../../components/meta/PageMeta';
import { StickySubNav } from '../../../components/nav/StickySubNav';
import { BannerHeader } from '../../user/BannerHeader';
import { useGetGalleryQuery } from '../../../services/endpoints/website/gallery';
import { getPartition } from '../../../utils/utils';
import Dots from '../../../assets/images/sfc-dots.png';
import girlScoutBadge from '../../../assets/images/privateEvents/girl-scouts-ambassador-dinner-party-badge-sticky-fingers-cooking.png';
import girlScoutImg from '../../../assets/images/privateEvents/girl-scouts-cooking-skills-sticky-fingers-cooking.jpg';
import eagleScoutBadge from '../../../assets/images/privateEvents/eagle-scout-cooking-merit-badge-sticky-fingers-cooking.png';
import boyScoutImg from '../../../assets/images/privateEvents/boy-scouts-cooking-badge-classes-sticky-fingers-cooking.jpg';
import './PrivateEvents.css';

const NAV_ITEMS = [
  { label: 'Girl Scout Badge Classes', id: 'girl-scout-badge-classes' },
  { label: 'Scout Video', id: 'scout-video' },
  { label: 'Scout Merit Badge Classes', id: 'scout-merit-badge-classes' },
  { label: 'Testimonials', id: 'scout-cooking-classes-testimonials' },
  { label: 'Gallery', id: 'scout-classes-gallery' },
];

const TESTIMONIALS = [
  { quote: "We had so much fun yesterday. The girls loved it! My daughter asked when she could do it again!", author: "Gina V., Junior Girl Scout Troop Leader, Pennsylvania" },
  { quote: "The girls had a really fun time. They enjoyed the class and the food.", author: "Jamie H., Colorado" },
  { quote: "The girls had a great time and learned something new. Thank you for such a great experience!", author: "Mary Lou E., Troop Leader, Illinois" },
  { quote: "All the girls had a great time, tried new things, and liked the recipes! Thumbs up all around from our participants.", author: "Junior Troop #1902, New York" },
];

const GirlScouts: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const { data: galleryData } = useGetGalleryQuery(2);

  return (
      <>
        <PageMeta
            title="Boy Scouts & Girl Scouts Badge Classes - Sticky Fingers Cooking"
            description="Earn cooking badges with Sticky Fingers Cooking! Fun, hands-on culinary badge classes for Girl Scouts and Boy Scouts of all ages, nationwide."
        />
        <BannerHeader title="Girl Scout & Boy Scout Cooking Classes" />

        <StickySubNav items={NAV_ITEMS} />

        {/* Girl Scout Section */}
        <div id="girl-scout-badge-classes" className="section-events-child">
          <Container>
            <div className="container-flex">
              <div className="div-block-events-large">
                <h2>Girl Scout Badge Classes</h2>
                <Image className="w-100 dots-divider my-2" src={Dots} />
                <p>At Sticky Fingers Cooking®, we make earning cooking badges easy and fun! Our cooking badge troop classes are available nationwide, offering hands-on culinary experiences that help scouts earn badges while having a blast. We'll come to your next troop meeting, bringing all the supplies and fresh ingredients, while accommodating all food allergies and serving up tasty, easy-to-make recipes that Girl Scouts love!</p>
                <ul className="w-list-unstyled">
                  <li><strong>Brownie Snack Badge:</strong> Explore a variety of healthy snacks that are delicious and easy to make!</li>
                  <li><strong>Junior Simple Meals Badge:</strong> Learn basic cooking skills that will help you make amazing and delicious meals for your family, all on your own.</li>
                  <li><strong>Cadette New Cuisines Badge:</strong> Let your taste buds travel the world as you cook up four dishes from new places. Discover culinary possibilities as diverse as the people who cook them.</li>
                  <li><strong>Senior Locavore Badge & Ambassador Dinner Party Badge:</strong> Learn the importance of eating locally, how it helps the environment and supports local farmers. Chefs are masters of building community at the table. For the dinner party badge, try on a chef's apron as you throw a great dinner party, bring people together, and practice creating multiple courses with finesse and flair.</li>
                </ul>
                <p>Classes are 90 minutes and include:</p>
                <ul className="w-list-unstyled">
                  <li>An amazing Sticky Fingers chef instructor</li>
                  <li>Instruction for 2-3 unique recipes specific to the badge</li>
                  <li>Certificate of completion and a fun craft</li>
                  <li>A digital family recipe collection for scouts to recreate the recipes at home</li>
                </ul>
                <p><strong>Prices vary by location —</strong> send us your troop details, and we'll cook you up a quote!</p>
                <a href="/contact" className="btn-events">Book Your Badge Class</a>
              </div>
              <div className="div-block-events-small">
                <div className="div-image">
                  <img src={girlScoutBadge} alt="Girl Scouts cooking merit badge" className="image-events" />
                </div>
                <div className="div-image">
                  <img src={girlScoutImg} alt="Group of girls learning how to cut food in a Sticky Fingers class" className="image-events rotate-left" />
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Scout Video Section */}
        <div id="scout-video" className="section-video">
          <Container>
            <Row className="align-items-center">
              <Col lg={4} className="mb-4 mb-lg-0">
                <div className="subheader white">scout merit badges</div>
                <h2>Sticky Fingers Cooking Scout Programs</h2>
                <p>Have an amazing culinary experience while earning your Cooking Merit Badges!</p>
                <a href="/contact" className="btn-events">Book Today</a>
              </Col>
              <Col lg={8}>
                <div className="video-wrapper">
                  <iframe
                      src="https://player.vimeo.com/video/590022606"                      allow="autoplay; fullscreen"
                      allowFullScreen
                      title="Scout Cooking Badge Video"
                  />
                </div>
              </Col>
            </Row>
          </Container>
        </div>

        {/* Boy Scout Section */}
        <div id="scout-merit-badge-classes" className="section-events-child alt">
          <Container>
            <div className="container-flex">
              <div className="div-block-events-large">
                <h2>Scout Merit Badge Classes</h2>
                <Image className="w-100 dots-divider my-2" src={Dots} />
                <ul className="w-list-unstyled">
                  <li><strong>Cooking Merit Badge:</strong> Scouts will learn health and safety, nutrition, cooking basics and will practice cooking at home. Scouts will not practice camp cooking but will discuss techniques.</li>
                </ul>
                <p>In order to fulfill the cooking merit badge requirements, scouts will participate in two 90-minute class sessions. Scheduling is flexible. Classes will include:</p>
                <ul className="w-list-unstyled">
                  <li>One of our amazing Sticky Fingers chef instructors</li>
                  <li>Instruction for 2-3 unique recipes specific to the badge</li>
                  <li>Certificate of completion</li>
                  <li>A digital family recipe collection for scouts to recreate the recipes at home</li>
                </ul>
                <p><strong>Prices vary by location —</strong> send us your troop details, and we'll cook you up a quote!</p>
                <a href="/contact" className="btn-events">Book Your Badge Class</a>
              </div>
              <div className="div-block-events-small">
                <div className="div-image">
                  <img src={eagleScoutBadge} alt="Eagle Scout cooking merit badge" className="image-events" />
                </div>
                <div className="div-image">
                  <img src={boyScoutImg} alt="Boy scouts cooking badge class" className="image-events rotate-left" />
                </div>
              </div>
            </div>
          </Container>
        </div>

        {/* Testimonials */}
        <section id="scout-cooking-classes-testimonials" className="section-testimonial-slider">
          <Container>
            <Row>
              <Col lg={7}>
                <Carousel
                    controls={false}
                    indicators={false}
                    interval={4500}
                    activeIndex={activeIndex}
                    onSelect={(i) => setActiveIndex(i)}
                >
                  {TESTIMONIALS.map((t, i) => (
                      <Carousel.Item key={i}>
                        <div className="text-quote">"{t.quote}"</div>
                        <div className="subheader white">{t.author}</div>
                      </Carousel.Item>
                  ))}
                </Carousel>
              </Col>
            </Row>
            <Row>
              <Col lg={7}>
                <div className="testimonial-dots">
                  {TESTIMONIALS.map((_, i) => (
                      <button
                          key={i}
                          className={`testimonial-dot${activeIndex === i ? ' active' : ''}`}
                          onClick={() => setActiveIndex(i)}
                      />
                  ))}
                </div>
              </Col>
            </Row>
          </Container>
        </section>

        {/* Gallery */}
        {galleryData?.gallery?.pictures && galleryData.gallery.pictures.length > 0 && (
            <div id="scout-classes-gallery" className="section-events-child">
              <Container>
                <div className="photo-grid">
                  {galleryData.gallery.pictures.map((pic, i) => (
                      <div key={i} className="photo-grid-item">
                        <img
                            src={`${pic.s3_base_url}/${getPartition(pic.id)}/original/${pic.image_file_name}`}
                            alt={pic.name ?? `Scout cooking class ${i + 1}`}
                        />
                      </div>
                  ))}
                </div>
              </Container>
            </div>
        )}
      </>
  );
};

export default GirlScouts;